import { IDomain } from '@store/services/projects/types';

export interface ITreeItem {
  id: string;
  name: string;
  children?: ITreeItem[];
}

export const buildTree = (data: IDomain[]): ITreeItem[] => {
  const treeMap: Record<string, ITreeItem> = {};

  data.forEach((node) => {
    treeMap[node.id] = { id: node.id, name: node.name, children: [] };
  });

  const domainItems = data.reduce((tree, node) => {
    if (node.parentId && treeMap[node.parentId]) {
      treeMap[node.parentId].children?.push(treeMap[node.id]);
    } else {
      return tree.concat(treeMap[node.id]);
    }

    return tree;
  }, [] as ITreeItem[]);

  return domainItems;
};

export const findParentIds = (
  tree: ITreeItem[],
  itemId: string,
  parentIds: string[] = [],
): string[] => {
  return tree.reduce((acc, item) => {
    if (item.id === itemId) {
      return acc;
    }

    if (item.children) {
      return [...acc, item.id, ...findParentIds(item.children, item.id)];
    }

    return acc;
  }, parentIds);
};

export const collectSubtreeIds = (node: ITreeItem): string[] => [
  node.id,
  ...(node.children?.flatMap(collectSubtreeIds) ?? []),
];

export const findSubtreeIds = (
  roots: ITreeItem[],
  nodeId: string,
): string[] | null => {
  for (const node of roots) {
    if (node.id === nodeId) {
      return collectSubtreeIds(node);
    }

    const found = findSubtreeIds(node.children ?? [], nodeId);

    if (found) {
      return found;
    }
  }

  return null;
};

const findAncestorTrail = (
  roots: ITreeItem[],
  nodeId: string,
  trail: string[],
): string[] | null => {
  for (const node of roots) {
    if (node.id === nodeId) {
      return trail;
    }

    const found = findAncestorTrail(node.children ?? [], nodeId, [
      ...trail,
      node.id,
    ]);

    if (found) {
      return found;
    }
  }

  return null;
};

export const findAncestorIds = (roots: ITreeItem[], nodeId: string): string[] =>
  findAncestorTrail(roots, nodeId, []) ?? [];

export type ApplyScopeSelectionOptions = {
  selected: string[];
  nodeId: string;
  isChecked: boolean;
  subtreeIds: string[];
  ancestorIds?: string[];
  clearsSubtreeOnUncheck?: boolean;
};

export const applyScopeSelection = ({
  selected,
  nodeId,
  isChecked,
  subtreeIds,
  ancestorIds = [],
  clearsSubtreeOnUncheck = false,
}: ApplyScopeSelectionOptions): string[] => {
  if (isChecked) {
    return Array.from(new Set([...selected, ...ancestorIds, ...subtreeIds]));
  }

  if (clearsSubtreeOnUncheck) {
    const removed = new Set(subtreeIds);

    return selected.filter((id) => !removed.has(id));
  }

  return selected.filter((id) => id !== nodeId);
};

export const transformDomainsToObject = (
  domains: IDomain[],
  domainIds: string[],
): Record<string, IDomain> => {
  const result = domains.reduce(
    (acc, domain) => {
      if (domainIds.includes(domain.id)) {
        return { ...acc, [domain.id]: domain };
      }

      return acc;
    },
    {} as Record<string, IDomain>,
  );

  return result;
};

export const getUploadedAssetsMessage = (created: number, updated: number) => {
  if (created && updated) {
    return `You added ${created} and updated ${updated} assets.`;
  }

  if (created) return `You added ${created} assets.`;
  if (updated) return `You updated ${updated} assets.`;
};

export const getUploadedAssetsStats = (created: number, updated: number) => {
  const statsBlocks = [
    {
      id: 'assets-imported',
      title: 'Number of parsed elements:',
      items: [{ text: 'Assets', value: created }],
    },
  ];

  if (updated) {
    return statsBlocks.concat([
      {
        id: 'assets-updated',
        title: 'Number of updated elements:',
        items: [{ text: 'Assets', value: updated }],
      },
    ]);
  }

  return statsBlocks;
};

const hasSelectedInSubtree = (
  node: ITreeItem,
  selected: Set<string>,
): boolean =>
  selected.has(node.id) ||
  (node.children ?? []).some((child) => hasSelectedInSubtree(child, selected));

const subtreeHasSelectedDomain = (
  node: ITreeItem,
  selected: Set<string>,
): boolean => {
  if (selected.has(node.id)) return true;
  return (
    node.children?.some((c) => subtreeHasSelectedDomain(c, selected)) ?? false
  );
};

const childSubtreeHasSelectedDomain = (
  node: ITreeItem,
  selected: Set<string>,
): boolean =>
  node.children?.some((c) => subtreeHasSelectedDomain(c, selected)) ?? false;

export const getScopeRowIndent = (depth: number) =>
  depth === 0 ? 0 : 12 * (depth + 1);

export type TreeCheckState = 'checked' | 'indeterminate' | 'unchecked';

export type GetTreeCheckStateOptions = {
  isDerivedFromSubtree?: boolean;
};

export const getTreeCheckState = (
  node: ITreeItem,
  selected: Set<string>,
  options: GetTreeCheckStateOptions = {},
): TreeCheckState => {
  const isSelfSelected = selected.has(node.id);

  if (!node.children?.length) {
    return isSelfSelected ? 'checked' : 'unchecked';
  }

  const childStates = node.children.map((child) =>
    getTreeCheckState(child, selected),
  );

  const isEveryChildChecked = childStates.every((state) => state === 'checked');

  if (isSelfSelected) {
    return isEveryChildChecked ? 'checked' : 'indeterminate';
  }

  if (!options.isDerivedFromSubtree) {
    return 'unchecked';
  }

  if (isEveryChildChecked) {
    return 'checked';
  }

  return childStates.every((state) => state === 'unchecked')
    ? 'unchecked'
    : 'indeterminate';
};

export const getDefaultExpandedForScopeTree = (
  roots: ITreeItem[],
  selectedIds: string[],
): string[] => {
  const selected = new Set(selectedIds);
  const expanded = new Set<string>();

  const visit = (node: ITreeItem, isRoot: boolean) => {
    const hasChildren = !!node.children?.length;
    if (!hasChildren) return;

    const shouldExpand = isRoot
      ? true
      : childSubtreeHasSelectedDomain(node, selected);

    if (shouldExpand) {
      expanded.add(node.id);
    }

    for (const child of node.children!) {
      visit(child, false);
    }
  };

  roots.forEach((root) => visit(root, true));
  return [...expanded];
};
